import { canPayResources } from "../resources/resource-pool";
import {
  getCardInstance,
  getCardInstanceDefinition,
  getEventDefinitionForInstance,
} from "../selectors/card-instances";
import { getEventLimitSnapshot } from "../selectors/event-limit";
import type {
  ActionValidationFailure,
  ActionValidationResult,
  GameState,
  PlayEventAction,
} from "../types";

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return { valid: false, failure: { code, message, path, details } };
}

export function validatePlayEventAction(
  state: Readonly<GameState>,
  action: PlayEventAction,
): ActionValidationResult {
  if (state.schemaVersion !== 5 || state.catalog.schemaVersion !== 4) {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      "Event resolution requires the public-beta V5 state and schema-4 catalog.",
      "schemaVersion",
    );
  }
  if (state.phase !== "action-window") {
    return invalid(
      "WRONG_PHASE",
      "An Event may be played only during its owner's action window.",
      "phase",
      { currentPhase: state.phase, requiredPhase: "action-window" },
    );
  }
  if (state.supplyStatus.type !== "resolved") {
    return invalid(
      "SUPPLY_UPKEEP_UNRESOLVED",
      "An Event cannot interrupt a mandatory Supply choice.",
      "supplyStatus",
    );
  }

  const player = state.players[action.playerId];
  const instance = getCardInstance(state, action.cardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected Event card instance does not exist.",
      "cardInstanceId",
      { cardInstanceId: action.cardInstanceId },
    );
  }
  if (instance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot play another player's Event.",
      `cardInstances.${action.cardInstanceId}.ownerPlayerId`,
    );
  }
  if (!player.hand.includes(action.cardInstanceId)) {
    return invalid(
      "CARD_NOT_IN_HAND",
      "The selected Event must be in the acting player's hand.",
      `players.${action.playerId}.hand`,
    );
  }
  const definition = getCardInstanceDefinition(state, action.cardInstanceId);
  if (definition?.type !== "event") {
    return invalid("CARD_NOT_EVENT", "Only an Event card may use play-event.", "cardInstanceId", {
      actualCardType: definition?.type ?? null,
    });
  }
  const eventDefinition = getEventDefinitionForInstance(state, action.cardInstanceId);
  if (eventDefinition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The Event lacks a schema-4 declarative effect definition.",
      `catalog.cards.${instance.cardDefinitionId}`,
    );
  }
  const limit = getEventLimitSnapshot(state, action.playerId);
  if (limit.remaining <= 0) {
    return invalid(
      "EVENT_LIMIT_REACHED",
      "The current Event Limit has been reached.",
      `players.${action.playerId}.actionsUsed.eventsPlayed`,
      { eventLimit: limit.limit, eventsPlayed: limit.eventsPlayed },
    );
  }
  if (!canPayResources(player.kingdomPool, eventDefinition.cost)) {
    return invalid(
      "INSUFFICIENT_KINGDOM_POOL",
      "The Kingdom Pool cannot pay the Event's exact printed cost.",
      `players.${action.playerId}.kingdomPool`,
      { cardDefinitionId: eventDefinition.id },
    );
  }
  return { valid: true };
}
